const DEFAULT_BASE = "https://wap.alphaxcloud.com/api";

const escapeForSingleQuotes = (value = "") =>
    String(value).replace(/\\/g, "\\\\").replace(/'/g, "'\\''");

const getBase = (apiBase) =>
    String(apiBase || process.env.REACT_APP_API_URL || DEFAULT_BASE).replace(/\/$/, "");

const defaults = (params = {}) => ({
    clientId: params.clientId || "YOUR_CLIENT_ID",
    token: params.token || localStorage.getItem("apiKey") || "YOUR_API_KEY",
    mobile: params.mobile || "923001234567",
    message: params.message || "Hello from AlphaXCloud!",
    groupId: params.groupId || "1203630xxxxx@g.us",
    contactName: params.contactName || "John Doe",
    contactPhone: params.contactPhone || "923001234567",
    latitude: params.latitude || "24.8607",
    longitude: params.longitude || "67.0011",
    filename: params.filename || "file.jpg",
    caption: params.caption || params.message || "Media caption",
});

const buildLangPack = ({ getUrl, postUrl, postBody, multipartHint, formFields }) => {
    const postBodyJson = JSON.stringify(postBody, null, 2);
    const postBodyOneLine = JSON.stringify(postBody);

    const curlParts = [];
    if (getUrl) {
        curlParts.push(`# Option A — GET (simple)\ncurl -X GET '${escapeForSingleQuotes(getUrl)}'`);
    }
    if (multipartHint) {
        const formFlags = (formFields || [])
            .map(([k, v]) => `  -F '${k}=${String(v).replace(/'/g, "'\\''")}'`)
            .join(" \\\n");
        curlParts.push(
            `# ${getUrl ? "Option B — " : ""}POST multipart\ncurl -X POST '${postUrl}' \\\n${formFlags}`
        );
    } else if (postUrl) {
        curlParts.push(
            `# ${getUrl ? "Option B — " : ""}POST\ncurl -X POST '${postUrl}' \\\n  -H 'Content-Type: application/json' \\\n  -d '${postBodyOneLine}'`
        );
    }

    const jsGet = getUrl
        ? `// Option A — GET
const getRes = await fetch(${JSON.stringify(getUrl)});
console.log(await getRes.json());
`
        : "";

    const jsPost = multipartHint
        ? `// ${getUrl ? "Option B — " : ""}POST multipart
const form = new FormData();
${(formFields || [])
    .map(([k, v]) =>
        k === "file"
            ? `form.append("file", fileInput.files[0]); // replace with your File`
            : `form.append(${JSON.stringify(k)}, ${JSON.stringify(String(v))});`
    )
    .join("\n")}

const postRes = await fetch(${JSON.stringify(postUrl)}, {
  method: "POST",
  body: form,
});
console.log(await postRes.json());`
        : `// ${getUrl ? "Option B — " : ""}POST
const postRes = await fetch(${JSON.stringify(postUrl)}, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(${postBodyJson}),
});
console.log(await postRes.json());`;

    const nodeGet = getUrl
        ? `// Option A — GET
const { data: getData } = await axios.get(${JSON.stringify(getUrl)});
console.log(getData);
`
        : "";

    const nodePost = multipartHint
        ? `// ${getUrl ? "Option B — " : ""}POST multipart
const FormData = require("form-data");
const fs = require("fs");
const form = new FormData();
${(formFields || [])
    .map(([k, v]) =>
        k === "file"
            ? `form.append("file", fs.createReadStream("./${v}"));`
            : `form.append(${JSON.stringify(k)}, ${JSON.stringify(String(v))});`
    )
    .join("\n")}

const { data } = await axios.post(${JSON.stringify(postUrl)}, form, {
  headers: form.getHeaders(),
});
console.log(data);`
        : `// ${getUrl ? "Option B — " : ""}POST
const { data } = await axios.post(
  ${JSON.stringify(postUrl)},
  ${postBodyJson},
  { headers: { "Content-Type": "application/json" } }
);
console.log(data);`;

    const pyGet = getUrl
        ? `# Option A — GET
r = requests.get(${JSON.stringify(getUrl)})
print(r.json())
`
        : "";

    const pyPost = multipartHint
        ? `# ${getUrl ? "Option B — " : ""}POST multipart
files = {"file": open(${JSON.stringify(formFields?.find(([k]) => k === "file")?.[1] || "file.jpg")}, "rb")}
payload = {
${(formFields || [])
    .filter(([k]) => k !== "file")
    .map(([k, v]) => `  ${JSON.stringify(k)}: ${JSON.stringify(String(v))},`)
    .join("\n")}
}
r2 = requests.post(${JSON.stringify(postUrl)}, data=payload, files=files)
print(r2.json())`
        : `# ${getUrl ? "Option B — " : ""}POST
r2 = requests.post(
  ${JSON.stringify(postUrl)},
  headers={"Content-Type": "application/json"},
  json=${postBodyJson},
)
print(r2.json())`;

    const phpGet = getUrl
        ? `// Option A — GET
$ch = curl_init(${JSON.stringify(getUrl)});
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
echo curl_exec($ch);
curl_close($ch);
`
        : "";

    const phpPost = multipartHint
        ? `// ${getUrl ? "Option B — " : ""}POST multipart
$post = [
${(formFields || [])
    .map(([k, v]) =>
        k === "file"
            ? `  'file' => new CURLFile(__DIR__ . '/${v}'),`
            : `  ${JSON.stringify(k)} => ${JSON.stringify(String(v))},`
    )
    .join("\n")}
];
$ch = curl_init(${JSON.stringify(postUrl)});
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_POSTFIELDS => $post,
]);
echo curl_exec($ch);
curl_close($ch);`
        : `// ${getUrl ? "Option B — " : ""}POST
$ch = curl_init(${JSON.stringify(postUrl)});
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => ['Content-Type: application/json'],
  CURLOPT_POSTFIELDS => json_encode(${JSON.stringify(postBody)}),
]);
echo curl_exec($ch);
curl_close($ch);`;

    return {
        curl: curlParts.join("\n\n"),
        javascript: `${jsGet}${jsPost}`.trim(),
        nodejs: `const axios = require("axios");\n\n${nodeGet}${nodePost}`.trim(),
        python: `import requests\n\n${pyGet}${pyPost}`.trim(),
        php: `<?php\n${phpGet}${phpPost}`.trim(),
    };
};

/** @param {'text'|'media'|'contact'|'location'|'group'} kind */
export const buildApiCodeSamples = (kind, params = {}) => {
    const p = defaults(params);
    const base = getBase(params.apiBase);

    if (kind === "text") {
        const getUrl = `${base}/send-message/chat?to=${encodeURIComponent(p.mobile)}&text=${encodeURIComponent(p.message)}&token=${encodeURIComponent(p.token)}&client_id=${encodeURIComponent(p.clientId)}`;
        const postUrl = `${base}/send-message/send`;
        const postBody = {
            client_id: p.clientId,
            token: p.token,
            mobile: p.mobile,
            type: "text",
            message: p.message,
            content: { text: p.message },
            fromTemplet: false,
            saveAsTemplet: false,
            onlySave: false,
        };
        return buildLangPack({ getUrl, postUrl, postBody });
    }

    if (kind === "media") {
        const postUrl = `${base}/send-message/send-media`;
        const formFields = [
            ["client_id", p.clientId],
            ["token", p.token],
            ["mobile", p.mobile],
            ["message", p.caption],
            ["type", "media"],
            ["fromTemplet", "false"],
            ["file", p.filename],
        ];
        return buildLangPack({
            postUrl,
            postBody: Object.fromEntries(formFields.filter(([k]) => k !== "file")),
            multipartHint: true,
            formFields,
        });
    }

    if (kind === "contact") {
        const postUrl = `${base}/send-message/send-contact`;
        const formFields = [
            ["client_id", p.clientId],
            ["token", p.token],
            ["mobile", p.mobile],
            ["name", p.contactName],
            ["phone", p.contactPhone],
            ["type", "contact"],
        ];
        return buildLangPack({
            postUrl,
            postBody: Object.fromEntries(formFields),
            multipartHint: true,
            formFields,
        });
    }

    if (kind === "location") {
        const postUrl = `${base}/send-message/send-location`;
        const formFields = [
            ["client_id", p.clientId],
            ["token", p.token],
            ["mobile", p.mobile],
            ["latitude", p.latitude],
            ["longitude", p.longitude],
            ["text", p.message],
            ["type", "location"],
        ];
        return buildLangPack({
            postUrl,
            postBody: Object.fromEntries(formFields),
            multipartHint: true,
            formFields,
        });
    }

    if (kind === "group") {
        const getUrl = `${base}/send-message/chat?to=${encodeURIComponent(p.groupId)}&text=${encodeURIComponent(p.message)}&token=${encodeURIComponent(p.token)}&client_id=${encodeURIComponent(p.clientId)}`;
        const postUrl = `${base}/send-message/send-to-group`;
        const postBody = {
            client_id: p.clientId,
            type: "type",
            typedMsg: p.message,
            groupData: { id: p.groupId },
        };
        // Group POST uses login JWT via Authorization header in dashboard;
        // for samples show JSON body + note Bearer in curl manually via buildLangPack JSON path.
        const pack = buildLangPack({ getUrl, postUrl, postBody });
        // Prefer documenting Bearer for group POST specifically
        pack.curl = `# Option A — GET (simple)
curl -X GET '${escapeForSingleQuotes(getUrl)}'

# Option B — POST (dashboard auth)
curl -X POST '${postUrl}' \\
  -H 'Authorization: Bearer YOUR_LOGIN_JWT' \\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify(postBody)}'`;
        return pack;
    }

    return buildApiCodeSamples("text", params);
};

export const CODE_SAMPLE_LANGS = [
    { id: "curl", label: "cURL" },
    { id: "javascript", label: "JavaScript" },
    { id: "nodejs", label: "Node.js" },
    { id: "python", label: "Python" },
    { id: "php", label: "PHP" },
];

export const CODE_SAMPLE_HINTS = {
    text: "Use your API key + phone number (country code, no +). Values update from the form.",
    media: "Multipart upload: send a file with caption via send-media. Values update from the form.",
    contact: "Send a vCard contact to a phone number. Values update from the form.",
    location: "Send latitude/longitude (+ optional caption). Values update from the form.",
    group: "Use your API key + group JID (@g.us). Values update from the form on the left.",
};
